// Dedicated, intent-focused service landing pages (/services/:slug)
// Content is factual capability description — no invented statistics, clients or results.

export const SERVICE_PAGES = [
  {
    slug: "custom-software-development",
    serviceId: "custom-software",
    name: "Custom Software Development",
    metaTitle: "Custom Software Development Company in Bangalore | Serpens Technology",
    metaDescription:
      "Custom software development in Bangalore for startups, SMEs and enterprises — ERP, CRM, SaaS, portals and workflow automation built around your processes. Get a free quote.",
    eyebrow: "Custom Software Development · Bengaluru",
    h1: "Custom software development company in Bangalore",
    intro: [
      "Serpens Technology designs and builds custom software for businesses in Bengaluru, across India and internationally. We replace spreadsheets, disconnected tools and ageing legacy systems with software built around the way your team actually works.",
      "Every engagement starts with a discovery call, a clear scope and an itemised proposal. You own the source code, documentation and IP from day one.",
    ],
    problems: [
      "Teams re-entering the same data into several tools",
      "Off-the-shelf software that forces workarounds and per-seat fees",
      "Approvals, billing or inventory still running on spreadsheets",
      "Legacy systems that are slow, insecure or impossible to extend",
      "No single dashboard to see what is happening in the business",
    ],
    offerings: [
      { title: "Enterprise software", desc: "Secure, role-based systems for operations, finance, HR and compliance teams." },
      { title: "SaaS product development", desc: "Multi-tenant SaaS platforms from MVP to scale — subscriptions, billing and admin built in." },
      { title: "ERP & CRM systems", desc: "Integrated ERP and CRM software tailored to your sales, inventory and accounting workflows." },
      { title: "Customer & employee portals", desc: "Self-service portals that cut support load and give users 24×7 access to their data." },
      { title: "Workflow & process automation", desc: "Automated approvals, notifications, documents and reports that remove repetitive manual work." },
      { title: "Dashboards & analytics", desc: "Business intelligence dashboards and reporting systems that turn raw data into decisions." },
    ],
    techs: ["React", "Next.js", "Node.js", "Python", "Java", "PHP / Laravel", "PostgreSQL", "MongoDB", "AWS", "Google Cloud"],
    industries: ["Manufacturing", "Healthcare", "Education", "Logistics", "Retail", "Real Estate", "Professional Services"],
    faqs: [
      { q: "How much does custom software development cost in Bangalore?", a: "Cost depends on scope, integrations and the number of user roles. After a free discovery call we send a fixed, itemised proposal so you know exactly what you are paying for — no hourly black boxes." },
      { q: "How long does it take to build custom software?", a: "A focused MVP typically ships in 8–12 weeks. Larger platforms such as ERPs are delivered in phases, so your team starts using working modules early instead of waiting for one big launch." },
      { q: "Who owns the source code?", a: "You do. Every project runs under NDA and you receive full ownership of the source code, documentation and intellectual property." },
      { q: "Can you modernise or take over our existing software?", a: "Yes. We start with a code and architecture audit, stabilise what exists, then modernise or extend it using our agile process with weekly demos." },
      { q: "Do you work with clients outside Bengaluru?", a: "Yes. We are headquartered in Bengaluru and work with clients across India and internationally through remote collaboration, shared dashboards and regular video demos." },
    ],
    related: ["erp-development", "crm-development", "ai-automation", "ui-ux-design"],
  },
  {
    slug: "web-development",
    serviceId: "web-development",
    name: "Website Development",
    metaTitle: "Website Development Company in Bangalore | Serpens Technology",
    metaDescription:
      "Web development company in Bangalore building fast, SEO-ready corporate websites, web apps, WordPress, React and Next.js sites that convert visitors into leads. Get a quote.",
    eyebrow: "Web Development · Bengaluru",
    h1: "Website & web development company in Bangalore",
    intro: [
      "We build fast, secure and search-friendly websites and web applications for businesses in Bengaluru, across India and worldwide — from corporate websites and landing pages to full web platforms.",
      "Every site is responsive, accessible and built with clean technical SEO foundations, so it loads quickly, ranks well and turns visitors into enquiries.",
    ],
    problems: [
      "An outdated website that does not reflect your business",
      "Slow page loads hurting search rankings and conversions",
      "A site that is hard for your team to update",
      "Traffic that does not convert into leads or sales",
    ],
    offerings: [
      { title: "Corporate & business websites", desc: "Professional websites that explain what you do and make it easy to contact you." },
      { title: "WordPress development", desc: "Custom WordPress themes and plugins your team can manage without a developer." },
      { title: "React & Next.js development", desc: "High-performance web apps and marketing sites on modern JavaScript frameworks." },
      { title: "Laravel & PHP web apps", desc: "Robust server-side web applications, admin panels and APIs." },
      { title: "Landing pages", desc: "Conversion-focused pages for campaigns, products and lead generation." },
      { title: "Website maintenance & modernisation", desc: "Updates, security patches, speed optimisation and redesigns of existing sites." },
    ],
    techs: ["WordPress", "React", "Next.js", "Laravel", "PHP", "Node.js", "Headless CMS", "Webflow", "Shopify"],
    industries: ["Education", "Professional Services", "Real Estate", "Healthcare", "Hospitality", "Startups"],
    faqs: [
      { q: "How long does it take to build a business website?", a: "A standard corporate website usually takes 3–6 weeks depending on the number of pages, content readiness and integrations. Larger web applications are scoped separately." },
      { q: "Will my website be SEO-friendly?", a: "Yes. We build with clean URLs, fast load times, mobile responsiveness, structured data, proper headings and metadata so search engines can crawl and understand your site." },
      { q: "Can I update the website myself?", a: "Yes. We use content management systems such as WordPress or a headless CMS so your team can edit pages, blogs and images without writing code." },
      { q: "Do you redesign existing websites?", a: "Yes. We audit your current site, keep what works, and modernise design, speed and content structure without losing your existing search visibility." },
    ],
    related: ["ecommerce-development", "ui-ux-design", "mobile-app-development", "custom-software-development"],
  },
  {
    slug: "mobile-app-development",
    serviceId: "mobile-apps",
    name: "Mobile App Development",
    metaTitle: "Mobile App Development Company in Bangalore | Android, iOS & Flutter",
    metaDescription:
      "Mobile app development company in Bangalore building Android, iOS, Flutter and React Native apps — customer, delivery, booking and enterprise apps. Book a discovery call.",
    eyebrow: "Mobile App Development · Bengaluru",
    h1: "Mobile app development company in Bangalore",
    intro: [
      "Serpens Technology builds Android, iOS and cross-platform mobile apps for startups and businesses in Bengaluru, India and abroad. We take apps from idea and UX design through development, testing, store launch and long-term support.",
      "We help you choose between native and cross-platform (Flutter or React Native) based on your budget, timeline and performance needs.",
    ],
    problems: [
      "Customers expecting to order, book or track from their phone",
      "Field teams relying on calls and paper instead of a mobile tool",
      "An existing app that crashes, is slow or has poor reviews",
      "Needing both Android and iOS without doubling the budget",
    ],
    offerings: [
      { title: "Android app development", desc: "Native Android apps in Kotlin, built for performance across devices." },
      { title: "iOS app development", desc: "Native iPhone and iPad apps in Swift that follow Apple's design guidelines." },
      { title: "Flutter & React Native apps", desc: "One codebase for Android and iOS — faster delivery and lower maintenance." },
      { title: "E-commerce & delivery apps", desc: "Catalogue, cart, payments, order tracking and delivery partner apps." },
      { title: "Booking & customer apps", desc: "Appointment, reservation and loyalty apps that keep customers engaged." },
      { title: "Enterprise & employee apps", desc: "Secure apps for field teams, attendance, inspections and internal workflows." },
    ],
    techs: ["Flutter", "React Native", "Kotlin", "Swift", "Firebase", "Node.js", "Razorpay", "Twilio"],
    industries: ["Retail", "E-commerce", "Logistics", "Healthcare", "Hospitality", "Startups"],
    faqs: [
      { q: "Should I build a native or cross-platform app?", a: "Cross-platform (Flutter or React Native) suits most business apps and saves time and cost. Native development is better when you need heavy device features or maximum performance. We recommend an approach after understanding your requirements." },
      { q: "How long does mobile app development take?", a: "A first version of a typical business app takes around 8–14 weeks including design, development, testing and store submission. Complex apps are delivered in phases." },
      { q: "Do you publish the app on Google Play and the App Store?", a: "Yes. We handle store listings, builds, review submission and release management for both stores." },
      { q: "Do you provide support after the app launches?", a: "Yes. We offer maintenance plans covering OS updates, bug fixes, monitoring and new features." },
    ],
    related: ["ui-ux-design", "custom-software-development", "ecommerce-development", "ai-automation"],
  },
  {
    slug: "ai-automation",
    serviceId: "ai-automation",
    name: "AI & Automation",
    metaTitle: "AI Development & Automation Company in Bangalore | Serpens Technology",
    metaDescription:
      "AI development company in Bangalore building AI chatbots, AI agents, RAG applications, OpenAI integrations and workflow automation that deliver practical business outcomes.",
    eyebrow: "AI & Automation · Bengaluru",
    h1: "AI development & business automation company in Bangalore",
    intro: [
      "We help businesses move beyond AI experiments and put AI to work on real processes — answering customers, processing documents, qualifying leads and automating repetitive operations.",
      "Our AI solutions are grounded in your own data, integrated with your existing systems, and built with access controls and human review where it matters.",
    ],
    problems: [
      "Support teams answering the same questions every day",
      "Staff manually reading, extracting and re-typing documents",
      "Knowledge scattered across files, emails and tools",
      "Repetitive back-office tasks slowing growth",
    ],
    offerings: [
      { title: "AI chatbots & customer support", desc: "Website and WhatsApp assistants that answer accurately from your own content." },
      { title: "AI agents", desc: "Agents that complete multi-step tasks across your CRM, email and internal tools." },
      { title: "RAG applications", desc: "Retrieval-augmented search and Q&A over your documents and knowledge base." },
      { title: "OpenAI & LLM integration", desc: "Add generation, summarisation and classification to existing software." },
      { title: "Document intelligence", desc: "Extract, validate and route data from invoices, forms and contracts." },
      { title: "Workflow automation", desc: "Rule- and AI-driven automation of approvals, notifications and data entry." },
    ],
    techs: ["OpenAI", "LangChain", "Vector Databases", "Python", "Node.js", "PostgreSQL", "MongoDB"],
    industries: ["Professional Services", "Healthcare", "E-commerce", "Finance", "Logistics", "Education"],
    faqs: [
      { q: "What kind of AI solutions do you build?", a: "We build AI chatbots, AI agents, retrieval-augmented (RAG) search over company documents, document data extraction, and AI features inside existing business software." },
      { q: "Is our data safe when using AI?", a: "We design solutions with role-based access, data minimisation and provider settings that keep your data private, and we can include human approval steps for sensitive actions." },
      { q: "Can AI integrate with our current CRM or ERP?", a: "Yes. We connect AI workflows to CRMs, ERPs, helpdesks, email and messaging platforms through their APIs." },
      { q: "How do we start an AI project?", a: "We begin with a short discovery to identify one high-value use case, build a focused pilot, measure it with your team, then expand." },
    ],
    related: ["custom-software-development", "crm-development", "erp-development", "web-development"],
  },
  {
    slug: "ui-ux-design",
    serviceId: "ui-ux",
    name: "UI/UX Design",
    metaTitle: "UI/UX Design Company in Bangalore | Product & App Design | Serpens",
    metaDescription:
      "UI/UX design services in Bangalore — user research, wireframes, prototypes and design systems for web apps, mobile apps, SaaS and enterprise software.",
    eyebrow: "UI/UX Design · Bengaluru",
    h1: "UI/UX design services in Bangalore",
    intro: [
      "Good software is easy to use. Our designers turn complex workflows into clear, intuitive interfaces for websites, mobile apps, SaaS products and enterprise systems.",
      "We validate designs with clickable prototypes before development starts, which reduces rework and helps users adopt the product quickly.",
    ],
    problems: [
      "Users struggling to find features or complete tasks",
      "Inconsistent screens across web and mobile",
      "Low adoption of internal tools",
      "Development rework caused by unclear requirements",
    ],
    offerings: [
      { title: "User research", desc: "Interviews and workflow analysis to understand how your users actually work." },
      { title: "Wireframes", desc: "Low-fidelity layouts that agree structure and flow before visual design." },
      { title: "Interactive prototypes", desc: "Clickable prototypes to test and sign off journeys early." },
      { title: "Web & mobile UI design", desc: "Polished, accessible interfaces for websites and mobile apps." },
      { title: "SaaS & enterprise UX", desc: "Dashboards, data-heavy screens and role-based experiences." },
      { title: "Design systems & branding", desc: "Reusable components and brand guidelines that keep products consistent." },
    ],
    techs: ["Figma", "Design Systems", "Prototyping", "Accessibility (WCAG)"],
    industries: ["Startups", "Education", "Healthcare", "Finance", "Professional Services"],
    faqs: [
      { q: "Do you offer UI/UX design as a separate service?", a: "Yes. You can engage us for design only, or for design and development together." },
      { q: "What do we receive at the end of a design project?", a: "Research findings, wireframes, high-fidelity designs, an interactive prototype and a component library or design system ready for developers." },
      { q: "Can you redesign our existing product?", a: "Yes. We review current usage and pain points, then redesign in stages so your users are not disrupted." },
    ],
    related: ["web-development", "mobile-app-development", "custom-software-development", "ecommerce-development"],
  },
  {
    slug: "ecommerce-development",
    serviceId: "web-development",
    name: "eCommerce Development",
    metaTitle: "eCommerce Website Development Company in Bangalore | Serpens Technology",
    metaDescription:
      "eCommerce development in Bangalore — Shopify, WooCommerce and custom online stores with payments, inventory and order management, plus eCommerce mobile apps.",
    eyebrow: "eCommerce Development · Bengaluru",
    h1: "eCommerce website development company in Bangalore",
    intro: [
      "We build online stores that are fast, secure and simple to manage — helping retailers and brands in Bengaluru and across India take their business online and sell with confidence.",
      "From Shopify and WooCommerce stores to fully custom eCommerce platforms, we connect payments, inventory, shipping and marketing tools into one smooth buying experience.",
    ],
    problems: [
      "Selling only offline or through marketplaces with high commissions",
      "A slow store with abandoned carts",
      "Manual order, stock and invoice management",
      "No mobile app for repeat customers",
    ],
    offerings: [
      { title: "Shopify stores", desc: "Branded Shopify stores with custom themes and apps." },
      { title: "WooCommerce development", desc: "WordPress-based stores with flexible product and checkout options." },
      { title: "Custom eCommerce platforms", desc: "Bespoke storefronts and admin for complex catalogues, B2B pricing or multi-vendor models." },
      { title: "Payment gateway integration", desc: "Razorpay and other gateways with secure checkout and refunds." },
      { title: "Inventory & order management", desc: "Stock, orders, invoices and shipping synced in one place." },
      { title: "eCommerce mobile apps", desc: "Android and iOS shopping apps with push notifications and loyalty." },
    ],
    techs: ["Shopify", "WooCommerce", "WordPress", "React", "Next.js", "Node.js", "Razorpay", "MongoDB"],
    industries: ["Retail", "E-commerce", "Agriculture", "Automotive", "Startups"],
    faqs: [
      { q: "Should I choose Shopify, WooCommerce or a custom store?", a: "Shopify suits most brands wanting a fast launch, WooCommerce suits businesses already on WordPress, and custom platforms suit complex catalogues, B2B pricing or marketplace models. We recommend based on your products and growth plans." },
      { q: "Can you integrate Razorpay and shipping partners?", a: "Yes. We integrate payment gateways such as Razorpay along with shipping, SMS/WhatsApp notifications and accounting tools." },
      { q: "Will my online store be mobile-friendly and SEO-ready?", a: "Yes. Every store is responsive and built with product schema, clean URLs and fast page loads." },
    ],
    related: ["web-development", "mobile-app-development", "crm-development", "ui-ux-design"],
  },
  {
    slug: "crm-development",
    serviceId: "custom-software",
    name: "CRM Development",
    metaTitle: "CRM Software Development Company in Bangalore | Custom CRM | Serpens",
    metaDescription:
      "Custom CRM development in Bangalore — lead management, sales pipelines, follow-up automation, WhatsApp/email integration and dashboards built around your sales process.",
    eyebrow: "CRM Development · Bengaluru",
    h1: "Custom CRM software development in Bangalore",
    intro: [
      "A CRM should match how your team sells, not the other way round. We build custom CRM software that captures every lead, automates follow-ups and shows you exactly where each deal stands.",
      "Our CRMs integrate with your website forms, WhatsApp, email, telephony and accounting tools, so customer information lives in one place.",
    ],
    problems: [
      "Leads lost in spreadsheets, inboxes and WhatsApp chats",
      "No visibility into the sales pipeline or team performance",
      "Generic CRMs that are expensive per user and hard to customise",
      "Manual follow-ups and quotations",
    ],
    offerings: [
      { title: "Lead & contact management", desc: "Capture leads from every source with de-duplication and assignment rules." },
      { title: "Sales pipeline", desc: "Custom stages, deal values and activity tracking for every opportunity." },
      { title: "Follow-up automation", desc: "Reminders, email and WhatsApp sequences triggered automatically." },
      { title: "Quotations & invoicing", desc: "Generate quotes and invoices directly from deals." },
      { title: "Industry CRMs", desc: "Real estate, dealer, education and service-business CRMs." },
      { title: "Reports & dashboards", desc: "Conversion, source and team performance reporting." },
    ],
    techs: ["React", "Node.js", "Python", "PostgreSQL", "MongoDB", "Twilio", "WhatsApp Business API"],
    industries: ["Real Estate", "Automotive", "Education", "Professional Services", "Retail"],
    faqs: [
      { q: "Why build a custom CRM instead of using an off-the-shelf one?", a: "A custom CRM fits your exact sales process, avoids per-user licence fees and integrates with the tools you already use. It becomes a business asset you own." },
      { q: "Can the CRM integrate with WhatsApp and our website?", a: "Yes. We integrate website forms, WhatsApp, email, telephony and payment tools so leads and conversations are captured automatically." },
      { q: "Can you migrate data from our current CRM or spreadsheets?", a: "Yes. We clean, map and migrate your existing contacts, deals and history." },
    ],
    related: ["custom-software-development", "erp-development", "ai-automation", "web-development"],
  },
  {
    slug: "erp-development",
    serviceId: "custom-software",
    name: "ERP Development",
    metaTitle: "ERP Software Development Company in Bangalore | Custom ERP | Serpens",
    metaDescription:
      "Custom ERP software development in Bangalore — inventory, purchase, sales, accounting, HR and manufacturing modules integrated into one system. Phased delivery, you own the code.",
    eyebrow: "ERP Development · Bengaluru",
    h1: "Custom ERP software development in Bangalore",
    intro: [
      "We build custom ERP software that connects purchasing, inventory, production, sales, accounts and HR into one system — designed around your operations rather than a generic template.",
      "ERPs are delivered in phases, starting with the modules that remove the most manual work, so your teams see value early.",
    ],
    problems: [
      "Departments working in separate tools and spreadsheets",
      "Stock mismatches and delayed purchase decisions",
      "No real-time view of production, sales or cash flow",
      "Generic ERPs that need expensive customisation",
    ],
    offerings: [
      { title: "Inventory & warehouse management", desc: "Multi-location stock, batches, transfers and reorder levels." },
      { title: "Purchase & vendor management", desc: "Requisitions, purchase orders, approvals and vendor records." },
      { title: "Sales, billing & POS", desc: "Orders, invoicing, GST-ready billing and point of sale." },
      { title: "Manufacturing ERP", desc: "Bills of materials, production planning and work orders." },
      { title: "HRMS & payroll", desc: "Employees, attendance, leave and payroll processing." },
      { title: "Accounting & reporting", desc: "Ledgers, receivables, payables and management dashboards." },
    ],
    techs: ["Java", "Python", "Node.js", "React", "PostgreSQL", "MySQL", "AWS", "Google Cloud"],
    industries: ["Manufacturing", "Retail", "Logistics", "Construction", "Healthcare", "Education"],
    faqs: [
      { q: "How long does a custom ERP take to build?", a: "ERPs are delivered in phases. The first core modules typically go live within a few months, with further modules added in later phases based on priority." },
      { q: "Can the ERP integrate with Tally or our accounting software?", a: "Yes. We integrate with accounting, payment, e-commerce and logistics systems through APIs or scheduled data exchange." },
      { q: "Is a custom ERP suitable for small and medium businesses?", a: "Yes. Because modules are built only for what you need, a custom ERP can be practical for SMEs and grows with the business." },
    ],
    related: ["custom-software-development", "crm-development", "ai-automation", "mobile-app-development"],
  },
];

export const getServicePage = (slug) => SERVICE_PAGES.find((s) => s.slug === slug);
// Map main service ids (data/content SERVICES) to their dedicated pages
export const SERVICE_ID_TO_SLUG = {
  "custom-software": "custom-software-development",
  "web-development": "web-development",
  "mobile-apps": "mobile-app-development",
  "ai-automation": "ai-automation",
  "ui-ux": "ui-ux-design",
};
